import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { WithdrawalsApiAdapter } from '../adapters';
import { WithdrawalsFilters } from '../models';
import { WITHDRAWALS_QUERY_KEY } from './useWithdrawals';

type ApiMeta = {
  total?: number;
  per_page?: number;
  current_page?: number;
  last_page?: number;
};

function normalizeWithdrawalsMeta(meta: ApiMeta | undefined): {
  total: number;
  currentPage: number;
  lastPage: number;
} {
  if (!meta) {
    return { total: 0, currentPage: 1, lastPage: 1 };
  }
  return {
    total: meta.total ?? 0,
    currentPage: meta.current_page ?? 1,
    lastPage: meta.last_page ?? 1,
  };
}

const DEFAULT_FILTERS: WithdrawalsFilters = {};

export const useWithdrawalsInfinite = (walletId: number | null | undefined, filters?: WithdrawalsFilters) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new WithdrawalsApiAdapter(httpClient), [httpClient]);
  const resolvedFilters = filters ?? DEFAULT_FILTERS;

  return useInfiniteQuery({
    queryKey: [WITHDRAWALS_QUERY_KEY, 'infinite', walletId, resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const response = await apiAdapter.getWithdrawals(walletId ?? null, {
        ...resolvedFilters,
        page: pageParam as number,
        perPage: 25,
      });
      return {
        data: response.data,
        meta: normalizeWithdrawalsMeta(response.meta),
      };
    },
    getNextPageParam: (lastPage) => {
      const { currentPage, lastPage: last } = lastPage.meta;
      if (currentPage < last) {
        return currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 60 * 1000,
    /** Misma UX que otras listas con AgGrid: vista previa mientras cambian filtros, no loader a pantalla completa. */
    placeholderData: keepPreviousData,
  });
};
